<template>
  <FormPage
    :title="`Исполнитель #${executorId}`"
    description="Просмотр исполнителя. Карточки дают быстрый доступ к редактированию, замене и проверкам безопасности."
  >
    <template #controls>
      <label class="split-toggle"><input v-model="split" type="checkbox" /> Раздельный режим</label>
      <button class="btn-outline back-list-button" type="button" @click="goToList">Назад к списку</button>
    </template>

    <div
      v-if="executor"
      class="hub-status"
      :class="executor.is_success ? 'hub-status-ok' : 'hub-status-bad'"
    >
      <span class="status-text">{{ executor.is_success ? 'Допущен' : 'Не допущен' }}</span>
      <span class="status-toggle">
        <success-executor
          v-if="!approval || canOverrideApproval"
          :success="executor.is_success"
          @update="chengeAllow"
        />
      </span>
    </div>

    <TmsLastErrorCard
      v-if="canViewTmsLog && tmsLastError"
      :error="tmsLastError"
      :log-link="tmsLogLink"
      class="hub-tms-error"
    />

    <my-loader v-if="isLoading" />

    <div v-else-if="!executor" class="empty-state">
      Исполнитель не найден.
      <button class="btn" type="button" @click="goToList">Вернуться к списку</button>
    </div>

    <ExecutorSplitWorkspace
      v-else-if="split"
      mode="detail"
      :executor="executor"
      :items="{}"
      :save-handler="saveSplitForm"
      :access-show="accessShow"
      :reload-handler="reloadExecutor"
      @replace="openReplace({ companies: 'company', managers: 'manager', drivers: 'driver', cars: 'car', trailers: 'trailer' }[$event])"
    >
      <template #summary>
        <div class="summary-row">
          <span class="summary-key">ID</span>
          <span class="summary-val">{{ executor.id }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-key">Создан</span>
          <span class="summary-val">{{ executor.created_at ? formatDate(executor.created_at) : '—' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-key">Обновлён</span>
          <span class="summary-val">{{ executor.updated_at ? formatDate(executor.updated_at) : '—' }}</span>
        </div>
        <div v-if="executor.driver_trips_count !== undefined" class="summary-row">
          <span class="summary-key">Рейсов водителя</span>
          <span class="summary-val">{{ executor.driver_trips_count }}</span>
        </div>
        <div v-if="approval" class="summary-row summary-row-warn">
          <span class="summary-key">Блокировка</span>
          <span class="summary-val">есть блокирующие ошибки проверок</span>
        </div>
        <p v-if="approval && !canOverrideApproval" class="approval-hint">
          Допуск заблокирован until ошибки проверок не будут устранены.
        </p>
        <ExecutorHubActions deletable :disabled="isDeleting" @delete="showConfirmationModal" />
      </template>
    </ExecutorSplitWorkspace>

    <FormGrid v-else :cols="3">
      <ExecutorHubCard title="Компания" :filled="!!executor.company" clickable @click="goStep('company')">
        <template #actions>
          <ExecutorHubActions editable replaceable @edit="goStep('company')" @replace="openReplace('company')" />
        </template>
        <ExecutorHubPreview model="companies" :item="executor.company" empty-text="Компания не назначена" />
      </ExecutorHubCard>

      <ExecutorHubCard title="Менеджер" :filled="!!executor.manager" clickable @click="goStep('manager')">
        <template #actions>
          <ExecutorHubActions editable replaceable @edit="goStep('manager')" @replace="openReplace('manager')" />
        </template>
        <ExecutorHubPreview model="managers" :item="executor.manager" empty-text="Менеджер не назначен" />
      </ExecutorHubCard>

      <ExecutorHubCard title="Водитель" :filled="!!executor.driver" clickable @click="goStep('driver')">
        <template #actions>
          <ExecutorHubActions editable replaceable @edit="goStep('driver')" @replace="openReplace('driver')" />
        </template>
        <ExecutorHubPreview model="drivers" :item="executor.driver" empty-text="Водитель не назначен" />
      </ExecutorHubCard>

      <ExecutorHubCard title="Автомобиль" :filled="!!executor.car" clickable @click="goStep('car')">
        <template #actions>
          <ExecutorHubActions editable replaceable @edit="goStep('car')" @replace="openReplace('car')" />
        </template>
        <ExecutorHubPreview model="cars" :item="executor.car" empty-text="Автомобиль не назначен" />
      </ExecutorHubCard>

      <ExecutorHubCard title="Прицеп" :filled="!!executor.trailer" clickable @click="goStep('trailer')">
        <template #actions>
          <ExecutorHubActions editable replaceable @edit="goStep('trailer')" @replace="openReplace('trailer')" />
        </template>
        <ExecutorHubPreview model="trailers" :item="executor.trailer" empty-text="Прицеп не назначен" />
      </ExecutorHubCard>

      <ExecutorHubCard title="Итог">
        <template #actions>
          <ExecutorHubActions deletable :disabled="isDeleting" @delete="showConfirmationModal" />
        </template>

        <div class="summary-row">
          <span class="summary-key">ID</span>
          <span class="summary-val">{{ executor.id }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-key">Создан</span>
          <span class="summary-val">{{ executor.created_at ? formatDate(executor.created_at) : '—' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-key">Обновлён</span>
          <span class="summary-val">{{ executor.updated_at ? formatDate(executor.updated_at) : '—' }}</span>
        </div>
        <div v-if="executor.driver_trips_count !== undefined" class="summary-row">
          <span class="summary-key">Рейсов водителя</span>
          <span class="summary-val">{{ executor.driver_trips_count }}</span>
        </div>
        <div v-if="approval" class="summary-row summary-row-warn">
          <span class="summary-key">Блокировка</span>
          <span class="summary-val">есть блокирующие ошибки проверок</span>
        </div>

        <p v-if="approval && !canOverrideApproval" class="approval-hint">
          Допуск заблокирован until ошибки проверок не будут устранены.
        </p>
      </ExecutorHubCard>
    </FormGrid>

    <modal-dialog :show="showReplaceModal" @update:show="onReplaceModalUpdate">
      <div class="replace-popup">
        <h3 class="replace-popup-title">{{ replaceTitle }}</h3>
        <p class="replace-popup-subtitle">{{ replaceSubtitle }}</p>
        <EntitySmartSearch
          :model="replaceModel"
          :itemSelect="replaceSelected"
          :companyId="replaceModel === 'managers' && executor.company ? executor.company.id : null"
          @update:itemSelect="onReplaceSelect"
        />
        <div v-if="replaceError" class="validation-error">{{ replaceError }}</div>
        <div class="replace-popup-actions">
          <button
            type="button"
            class="replace-popup-cancel"
            :disabled="replaceSaving"
            @click="closeReplace"
          >Отмена</button>
          <button
            type="button"
            class="replace-popup-confirm"
            :disabled="!replaceSelected || replaceSaving"
            @click="confirmReplace"
          >{{ replaceSaving ? 'Сохранение…' : 'Подтвердить замену' }}</button>
        </div>
      </div>
    </modal-dialog>

    <div
      v-if="showModal"
      class="delete-modal"
      @click.self="hideConfirmationModal"
      @keydown.esc="hideConfirmationModal"
    >
      <div class="delete-modal__panel" role="dialog" aria-modal="true" aria-labelledby="delete-executor-title" tabindex="-1" @keydown.esc="hideConfirmationModal">
        <h2 id="delete-executor-title">Удалить исполнителя?</h2>
        <p>
          Исполнитель № {{ executor && executor.id }} будет удалён безвозвратно.
          Действие нельзя отменить.
        </p>
        <div class="delete-modal__actions">
          <button
            type="button"
            class="delete-modal__cancel"
            :disabled="isDeleting"
            @click="hideConfirmationModal"
          >
            Отмена
          </button>
          <button
            type="button"
            class="delete-modal__confirm"
            :disabled="isDeleting"
            @click="deleteExecutor"
          >
            {{ isDeleting ? 'Удаление…' : 'Удалить' }}
          </button>
        </div>
      </div>
    </div>
  </FormPage>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { createToaster } from '@meforma/vue-toaster'
import FormPage from '@/components/formKit/FormPage.vue'
import FormGrid from '@/components/formKit/FormGrid.vue'
import EntitySmartSearch from '@/components/UI/EntitySmartSearch.vue'
import ExecutorHubActions from '@/components/UI/ExecutorHubActions.vue'
import ExecutorHubCard from '@/components/UI/ExecutorHubCard.vue'
import ExecutorHubPreview from '@/components/UI/ExecutorHubPreview.vue'
import ExecutorSplitWorkspace from '@/components/UI/ExecutorSplitWorkspace.vue'
import TmsLastErrorCard from '@/components/TmsLastErrorCard.vue'
import { fetchTmsActionLogByExecutor } from '@/backend/tmsActionLogApi'
import { resolveDeleteExecutorResult } from '@/pages/securityCheck/helpers/deleteExecutorResult.mjs'
import { useExecutorSplitMode } from '@/composables/useExecutorSplitMode'

const STEP_MODEL = {
  company: 'company',
  manager: 'manager',
  driver: 'driver',
  car: 'car',
  trailer: 'trailer',
}

const STEP_LABELS = {
  company: 'Компанию',
  manager: 'Менеджера',
  driver: 'Водителя',
  car: 'Автомобиль',
  trailer: 'Прицеп',
}

const REPLACE_SEARCH_MODEL = {
  company: 'companies',
  manager: 'managers',
  driver: 'drivers',
  car: 'cars',
  trailer: 'trailers',
}

export default {
  name: 'ExecutorDetailHub',
    components: {
      FormPage,
      FormGrid,
      ExecutorHubActions,
      ExecutorHubCard,
      ExecutorHubPreview,
      ExecutorSplitWorkspace,
      EntitySmartSearch,
      TmsLastErrorCard,
    },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const toaster = createToaster({ position: 'top-right' })
    const model = 'executors'
    const { split } = useExecutorSplitMode()

    const executorId = computed(() => route.params.id)
    const executor = computed(() => store.state.executors.executors.item.data)
    const isLoading = computed(() => store.state.executors.executors.item.loading)
    const approval = computed(() => store.getters['executors/getApproval'])
    const user = computed(() => store.state.user.user)
    const canOverrideApproval = computed(() => {
      const role = user.value && user.value.role && user.value.role.name
      return role && role !== 'Менеджер'
    })

    const showModal = ref(false)
    const isDeleting = ref(false)
    const warningKey = ref(0)

    const showReplaceModal = ref(false)
    const replaceStep = ref(null)
    const replaceSelected = ref(null)
    const replaceSaving = ref(false)
    const replaceError = ref('')

    const replaceModel = computed(() => REPLACE_SEARCH_MODEL[replaceStep.value] || 'companies')
    const replaceTitle = computed(() => replaceStep.value
      ? `Замена: ${STEP_LABELS[replaceStep.value]}`
      : 'Замена')
    const replaceSubtitle = computed(() => replaceStep.value
      ? `Выберите существующего для замены у исполнителя #${executorId.value}.`
      : '')

    const companySecurity = computed(() => firstSecurity(executor.value && executor.value.company))
    const managerSecurity = computed(() => firstSecurity(executor.value && executor.value.manager))
    const driverSecurity = computed(() => firstSecurity(executor.value && executor.value.driver))
    const carSecurity = computed(() => firstSecurity(executor.value && executor.value.car))
    const trailerSecurity = computed(() => firstSecurity(executor.value && executor.value.trailer))

    const CAR_OWNERSHIP_ERROR_MSG = 'Нет данных о периоде владения автомобилем'
    const managerErrors = computed(() => {
      const errors = executor.value && executor.value.errors
      if (!Array.isArray(errors)) return []
      return errors.filter((e) => {
        const msg = e && typeof e === 'object' ? e.msg : e
        return !String(msg || '').includes(CAR_OWNERSHIP_ERROR_MSG)
      })
    })

    function firstSecurity(entity) {
      if (!entity || !Array.isArray(entity.security_info) || !entity.security_info.length) return null
      return entity.security_info[0] || null
    }

    onMounted(async () => {
      await store.dispatch('executors/getItem', { model, id: route.params.id, silence: false })
      loadTmsLastError()
    })

    const canViewTmsLog = computed(() => store.getters.hasPermission('tms.action_log'))
    const tmsLastError = ref(null)
    const tmsLogLink = computed(() => {
      const err = tmsLastError.value
      const tripId = err && (err.cargo_trip_trip_id || err.trip_id)
      return tripId
        ? `/admin/tms-action-log?trip_id=${encodeURIComponent(tripId)}`
        : '/admin/tms-action-log'
    })
    async function loadTmsLastError() {
      if (!canViewTmsLog.value || !executorId.value) return
      try {
        const data = await fetchTmsActionLogByExecutor(executorId.value, {
          status: 'error',
          page: 1,
          per_page: 1,
        })
        tmsLastError.value = (data && data.items && data.items[0]) || null
      } catch (err) {
        // ошибка загрузки не блокирует страницу исполнителя
      }
    }

    function goToList() {
      router.push({ name: 'Executors' })
    }

    function goStep(step) {
      router.push({ name: 'ExecutorDetailStep', params: { id: String(executorId.value), step } })
    }

    async function reloadExecutor() {
      await store.dispatch('executors/getItem', { model, id: route.params.id, silence: false })
      warningKey.value += 1
    }

    async function saveSplitForm(step, payload) {
      const entityModel = { companies: 'companies', managers: 'managers', drivers: 'drivers', cars: 'cars', trailers: 'cars' }[step]
      const response = step === 'drivers'
        ? await store.dispatch('executors/updateSecure', { form: payload, model: entityModel })
        : await store.dispatch('apiStorage/updateItem', { model: entityModel, form: payload })
      if (!response) return false
      await reloadExecutor()
      return true
    }

    function showConfirmationModal() {
      showModal.value = true
    }

    function hideConfirmationModal() {
      if (isDeleting.value) return
      showModal.value = false
    }

    async function deleteExecutor() {
      if (isDeleting.value) return
      isDeleting.value = true
      try {
        const payload = await store.dispatch('executors/deleteExecutor', {
          id: route.params.id,
        })
        const result = resolveDeleteExecutorResult(payload)
        if (result.ok) {
          toaster.success(result.message)
          showModal.value = false
          await router.push('/executors')
          return
        }
        toaster.error(result.message)
        showModal.value = false
      } finally {
        isDeleting.value = false
      }
    }

    function openReplace(step) {
      replaceStep.value = step
      replaceSelected.value = null
      replaceError.value = ''
      showReplaceModal.value = true
    }

    function closeReplace() {
      if (replaceSaving.value) return
      showReplaceModal.value = false
      replaceStep.value = null
      replaceSelected.value = null
      replaceError.value = ''
    }

    function onReplaceModalUpdate(value) {
      if (!value) closeReplace()
    }

    function onReplaceSelect(entity) {
      replaceSelected.value = entity
      replaceError.value = ''
    }

    async function confirmReplace() {
      if (!replaceSelected.value || replaceSaving.value) return
      replaceSaving.value = true
      replaceError.value = ''
      try {
        const updateForm = {
          id: Number(route.params.id),
          [STEP_MODEL[replaceStep.value]]: replaceSelected.value,
        }
        await store.dispatch('executors/updateItem', {
          model: 'executors',
          id: route.params.id,
          form: updateForm,
        })
        toaster.success(`${STEP_LABELS[replaceStep.value]} заменён`)
        showReplaceModal.value = false
        replaceStep.value = null
        replaceSelected.value = null
        await reloadExecutor()
      } catch (err) {
        const detail = err?.response?.data?.detail
        if (Array.isArray(detail)) {
          replaceError.value = detail.map(d => d.msg).join('; ')
        } else if (typeof detail === 'string') {
          replaceError.value = detail
        } else {
          replaceError.value = err?.message || 'Не удалось обновить исполнителя'
        }
      } finally {
        replaceSaving.value = false
      }
    }

    async function chengeAllow(value) {
      try {
        await store.dispatch('executors/updateItem', {
          model: 'executors',
          id: route.params.id,
          form: { id: Number(route.params.id), is_success: value },
        })
        toaster.success(value ? 'Исполнитель допущен' : 'Допуск снят')
        await reloadExecutor()
      } catch (err) {
        const detail = err?.response?.data?.detail
        const message = Array.isArray(detail)
          ? detail.map(d => d.msg).join('; ')
          : (typeof detail === 'string' ? detail : (err?.message || 'Не удалось изменить статус'))
        toaster.error(message)
      }
    }

    async function confirmManager(value) {
      const manager = executor.value && executor.value.manager
      if (!manager) return
      try {
        const managerForm = { id: manager.id, relation_company: value }
        await store.dispatch('executors/confirmManager', { managerForm })
        toaster.success('Подтверждение связи менеджера обновлено')
        await reloadExecutor()
      } catch (err) {
        const detail = err?.response?.data?.detail
        const message = Array.isArray(detail)
          ? detail.map(d => d.msg).join('; ')
          : (typeof detail === 'string' ? detail : (err?.message || 'Не удалось подтвердить менеджера'))
        toaster.error(message)
      }
    }

    function accessShow(name) {
      const e = executor.value
      if (!e) return false
      try {
        if (name === 'company') {
          const s = companySecurity.value
          return !!(s && (s.dadata_is_end || s.ati_is_end))
        }
        if (name === 'car') {
          const s = carSecurity.value
          return !!(s && s.avtocod_is_end)
        }
        if (name === 'trailer') {
          const s = trailerSecurity.value
          return !!(s && s.avtocod_is_end)
        }
        if (name === 'driver') {
          const s = driverSecurity.value
          const cs = companySecurity.value
          return !!((s && (s.dadata_is_end || (cs && cs.nalog_is_end))))
        }
        if (name === 'manager') {
          const s = managerSecurity.value
          return !!(s && s.ati_is_end)
        }
      } catch (_err) {
        return false
      }
      return false
    }

    function securityLineText(security) {
      if (!security) return '—'
      const flags = []
      if (security.black_list || security.black_list_client) flags.push('ЧС')
      return flags.length ? flags.join(', ') : 'без_flags'
    }

    function securityLineClass(security) {
      if (!security) return ''
      if (security.black_list || security.black_list_client) return 'security-warn'
      return 'security-ok'
    }

    function innCheckClass(status) {
      if (status === 'done') return 'security-ok'
      if (status === 'failed') return 'security-warn'
      return 'security-pending'
    }

    function innCheckText(status) {
      if (status === 'pending') return 'ожидает проверки'
      if (status === 'processing') return 'проверяется'
      if (status === 'done') return 'проверен'
      if (status === 'failed') return 'ошибка проверки'
      return status
    }

    function shortCompanyName(name) {
      if (!name) return ''
      return String(name).replace('Индивидуальный предприниматель', 'ИП')
    }

    function formatDriverName(driver) {
      if (!driver) return '—'
      const parts = [driver.last_name, driver.first_name, driver.surname].filter(Boolean)
      if (parts.length) return parts.join(' ')
      return driver.first_name || driver.name || '—'
    }

    function shortPassport(passport) {
      if (!passport) return '—'
      const value = String(passport).trim()
      if (value.length <= 12) return value
      return value.slice(0, 10) + '…'
    }

    function shortVin(vin) {
      if (!vin) return '—'
      const value = String(vin).trim()
      if (value.length <= 12) return value
      return value.slice(0, 10) + '…'
    }

    function formatDate(value) {
      if (!value) return '—'
      try {
        return new Date(value).toLocaleDateString('ru-RU', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        })
      } catch {
        return String(value)
      }
    }

    return {
      executorId,
      split,
      executor,
      isLoading,
      approval,
      canOverrideApproval,
      showModal,
      isDeleting,
      showReplaceModal,
      replaceStep,
      replaceSelected,
      replaceSaving,
      replaceError,
      replaceModel,
      replaceTitle,
      replaceSubtitle,
      companySecurity,
      managerSecurity,
      driverSecurity,
      carSecurity,
      trailerSecurity,
      managerErrors,
      goToList,
      goStep,
      reloadExecutor,
      saveSplitForm,
      showConfirmationModal,
      hideConfirmationModal,
      deleteExecutor,
      openReplace,
      closeReplace,
      onReplaceModalUpdate,
      onReplaceSelect,
      confirmReplace,
      chengeAllow,
      confirmManager,
      accessShow,
      securityLineText,
      securityLineClass,
      innCheckClass,
      innCheckText,
      shortCompanyName,
      formatDriverName,
      shortPassport,
      shortVin,
      formatDate,
      canViewTmsLog,
      tmsLastError,
      tmsLogLink,
    }
  },
}
</script>

<style scoped>
.btn {
  height: 32px;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  padding: 0 12px;
}

.split-toggle { color: #374151; font-size: 12px; }
.split-toggle input { margin-right: 6px; }

.btn:hover {
  border-color: #007bff;
  color: #007bff;
}

.back-list-button {
  align-items: center;
  background: #fff;
  border: 1px solid #d7dce2;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  justify-content: center;
  padding: 10px 16px;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
}

.back-list-button:hover {
  background: #f9fafb;
  border-color: #6c757d;
  color: #111827;
}

.hub-status {
  align-items: center;
  border: 1px solid transparent;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  color: #fff;
  display: flex;
  gap: 14px;
  margin-bottom: 16px;
  padding: 14px 18px;
}

.hub-status-ok {
  background: #16a34a;
  border-color: #16a34a;
}

.hub-status-bad {
  background: #dc3545;
  border-color: #dc3545;
}

.hub-tms-error {
  margin-bottom: 16px;
}

.status-text {
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.status-toggle {
  align-items: center;
  display: inline-flex;
  margin-left: auto;
}

.status-toggle :deep(.icon) {
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.55);
  color: #fff;
}

.status-toggle :deep(.denied) {
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.status-toggle :deep(.hover:hover) {
  background: rgba(255, 255, 255, 0.32);
}

.status-badge {
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
}

.status-badge-ok {
  background: rgba(40, 167, 69, 0.15);
  color: #1f7a34;
}

.status-badge-pending {
  background: rgba(255, 193, 7, 0.18);
  color: #8a6300;
}

.status-meta {
  color: #6b7280;
  font-size: 13px;
  margin-left: auto;
}

.status-meta.approval-flag {
  color: #b42318;
  font-weight: 600;
  margin-left: 0;
}

.hub-card {
  position: relative;
}

.hub-card-clickable {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.hub-card-clickable:hover {
  border-color: #6366f1;
  box-shadow: 0 12px 28px rgba(99, 102, 241, 0.18);
  transform: translateY(-1px);
}

.hub-card-clickable :deep(.form-card-header) {
  position: relative;
  z-index: 2;
}

.hub-card-clickable :deep(.form-card-title) {
  cursor: pointer;
}

.hub-card-title {
  align-items: center;
  color: #111827;
  display: flex;
  font-size: 18px;
  font-weight: 700;
  gap: 6px;
  margin: 0 0 12px;
  position: relative;
  z-index: 1;
}

.card-title-text {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.card-header-actions {
  align-items: center;
  display: inline-flex;
  gap: 6px;
  margin-left: auto;
}

.ok-icon {
  color: #28a745;
  font-size: 14px;
}

.preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  z-index: 1;
}

.preview-row {
  align-items: baseline;
  display: flex;
  gap: 8px;
}

.preview-key {
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  min-width: 92px;
}

.preview-val {
  color: #111827;
  font-size: 13px;
  overflow-wrap: anywhere;
}

.preview-val.preview-val-truncate {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.preview-val.security-ok {
  color: #1f7a34;
  font-weight: 600;
}

.preview-val.security-warn {
  color: #b42318;
  font-weight: 600;
}

.preview-val.security-pending {
  color: #8a6300;
  font-weight: 600;
}

.preview-val.security-line {
  font-size: 12px;
}

.preview-empty {
  color: #9ca3af;
  font-size: 13px;
  margin: 0;
  position: relative;
  z-index: 1;
}

.card-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  position: relative;
  z-index: 2;
}

.action-btn {
  align-items: center;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  cursor: pointer;
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  padding: 6px 12px;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.action-btn-sm {
  font-size: 11px;
  padding: 4px 8px;
}

.action-edit:hover:not(:disabled) {
  border-color: #2563eb;
  color: #2563eb;
}

.action-replace {
  border-color: #4f46e5;
  color: #4f46e5;
}

.action-replace:hover:not(:disabled) {
  background: #4f46e5;
  color: #fff;
}

.action-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.action-delete {
  border-color: #c45c5c;
  color: #b42318;
}

.action-delete:hover:not(:disabled) {
  background: #b42318;
  border-color: #b42318;
  color: #fff;
}

.action-btn:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.summary-row {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.summary-row-warn .summary-val {
  color: #b42318;
}

.summary-key {
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  min-width: 92px;
}

.summary-val {
  color: #111827;
  font-size: 13px;
}

.approval-hint {
  color: #8a6300;
  font-size: 12px;
  margin: 8px 0 0;
}

.empty-state {
  align-items: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  color: #6b7280;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 16px;
  padding: 32px;
}

.replace-popup {
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 460px;
  padding: 0 2px;
}

.replace-popup-title {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
}

.replace-popup-subtitle {
  color: #6b7280;
  font-size: 13px;
  margin: 0;
}

.replace-popup-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 8px;
}

.replace-popup-cancel,
.replace-popup-confirm {
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
}

.replace-popup-cancel {
  background: #fff;
  border-color: #d1d5db;
  color: #374151;
}

.replace-popup-cancel:hover:not(:disabled) {
  background: #f9fafb;
}

.replace-popup-confirm {
  background: #4f46e5;
  color: #fff;
}

.replace-popup-confirm:hover:not(:disabled) {
  background: #4338ca;
}

.replace-popup-cancel:disabled,
.replace-popup-confirm:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.validation-error {
  color: #b42318;
  font-size: 13px;
}

.delete-modal {
  position: fixed;
  inset: 0;
  z-index: 1100;
  align-items: center;
  background: rgba(17, 24, 39, 0.45);
  display: flex;
  justify-content: center;
  padding: 16px;
}

.delete-modal__panel {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
  padding: 24px;
  width: min(100%, 420px);
}

.delete-modal__panel h2 {
  color: #111827;
  font-size: 20px;
  margin: 0 0 10px;
}

.delete-modal__panel p {
  color: #4b5563;
  line-height: 1.45;
  margin: 0;
}

.delete-modal__actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 22px;
}

.delete-modal__cancel,
.delete-modal__confirm {
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  min-width: 110px;
  padding: 9px 16px;
}

.delete-modal__cancel {
  background: #fff;
  border-color: #d1d5db;
  color: #374151;
}

.delete-modal__cancel:hover:not(:disabled) {
  background: #f9fafb;
}

.delete-modal__confirm {
  background: #b42318;
  color: #fff;
}

.delete-modal__confirm:hover:not(:disabled) {
  background: #912018;
}

.delete-modal__cancel:disabled,
.delete-modal__confirm:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

@media (max-width: 1100px) {
  :deep(.form-grid) {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
}

@media (max-width: 720px) {
  :deep(.form-grid) {
    grid-template-columns: 1fr !important;
  }

  .replace-popup {
    min-width: 260px;
  }
}
</style>
